'use client';

import type { InfiniteData, QueryKey, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { PageData } from '@/types/infinite-query';

import { useQueryConfig } from './useQueryConfig';

/**
 * Configuration for creating an infinite query hook
 */
export type CreateInfiniteQueryConfig<
  TParams,
  TResponse extends PageData<unknown>,
> = {
  /** Function to create the query key from params */
  queryKeyFn: (params: TParams) => QueryKey;
  /** Function to fetch data with pagination */
  queryFn: (params: TParams, pageParam?: string) => Promise<TResponse>;
  /** Function to build params with pagination cursor */
  buildPaginatedParams: (params: TParams, pageParam: string | undefined) => TParams;
};

/**
 * Creates an infinite query hook with standardized pagination handling
 * Reduces boilerplate for cursor-based pagination queries
 *
 * @example
 * ```ts
 * const useInfiniteProfileListQuery = createInfiniteQueryHook({
 *   queryKeyFn: infiniteProfileListKeys,
 *   queryFn: (params) => getProfileListService(params),
 *   buildPaginatedParams: (params, pageParam) => ({
 *     ...params,
 *     params: {
 *       ...params.params,
 *       query: { ...params.params?.query, paginationAfter: pageParam },
 *     },
 *   }),
 * });
 * ```
 */
export function createInfiniteQueryHook<
  TParams,
  TNode,
  TResponse extends PageData<TNode>,
>(config: CreateInfiniteQueryConfig<TParams, TResponse>) {
  const { queryKeyFn, queryFn, buildPaginatedParams } = config;

  return function useInfiniteQueryHook(
    params: TParams,
    enabled: boolean = true,
  ): UseInfiniteQueryResult<InfiniteData<TResponse, unknown>, Error> {
    const { isEnabled } = useQueryConfig();

    return useInfiniteQuery<TResponse>({
      queryKey: queryKeyFn(params),
      queryFn: async ({ pageParam }) => {
        const paginatedParams = buildPaginatedParams(
          params,
          pageParam ? String(pageParam) : undefined,
        );
        return queryFn(paginatedParams);
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => {
        if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
          return lastPage.pageInfo.endCursor;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
          return firstPage.pageInfo.startCursor;
        }
        return undefined;
      },
      enabled: isEnabled && enabled,
    });
  };
}

/**
 * Standard function to build paginated params for most API calls
 * Adds paginationAfter to the query params
 */
export function buildStandardPaginatedParams<
  TParams extends {
    params?: {
      query?: Record<string, unknown>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  },
>(params: TParams, pageParam: string | undefined): TParams {
  return {
    ...params,
    params: {
      ...params.params,
      query: {
        ...params.params?.query,
        paginationAfter: pageParam,
      },
    },
  } as TParams;
}
